import React from "react";
import { FormHelperText } from "@mui/material";
import type { Theme } from "@mui/material/styles";
import CustomFormLabel from "@components/FormItem/FormItem.styles";
import Input from "@components/Input/Input";
import { normalizeDrawerText } from "@components/SideDrawer/SideDrawer.schema";
import { getSideDrawerStyles } from "@components/SideDrawer/SideDrawer.styles";
import {
  getReasonTextareaStyles,
  isPrintableDrawerKey,
  REASON_TEXT_MAX_CHARS,
} from "./bulkSecurityClearanceDrawerShared";

type BulkSecurityClearanceReasonFieldProps = {
  theme: Theme;
  reason: string;
  reasonLabel: string;
  reasonRequiredLabel: string;
  reasonPlaceholder: string;
  reasonError?: string;
  testIdPrefix: string;
  onReasonChange: (value: string) => void;
  onReasonError: (error?: string) => void;
  validateReason: (value: string) => string | undefined;
  t: (key: string, opts?: Record<string, unknown>) => string;
};

const BulkSecurityClearanceReasonField: React.FC<BulkSecurityClearanceReasonFieldProps> = ({
  theme,
  reason,
  reasonLabel,
  reasonRequiredLabel,
  reasonPlaceholder,
  reasonError,
  testIdPrefix,
  onReasonChange,
  onReasonError,
  validateReason,
  t,
}) => {
  const styles = getSideDrawerStyles(theme);
  const { inputSlotProps, sxProps, charCounterSx } = getReasonTextareaStyles(theme);

  return (
    <div className="col-span-full mb-0 flex flex-col gap-2 mt-6">
      <CustomFormLabel>{reasonRequiredLabel}</CustomFormLabel>
      <Input
        placeholder={reasonPlaceholder}
        multiline
        minRows={4}
        fullWidth
        variant="outlined"
        value={reason}
        onKeyDown={(event) => {
          if (!isPrintableDrawerKey(event)) return;
          if (reason.length >= REASON_TEXT_MAX_CHARS) {
            onReasonError(
              t("form-validation.max-length", {
                label: reasonLabel,
                max: REASON_TEXT_MAX_CHARS,
              }),
            );
          }
        }}
        onChange={(event) => {
          const value = event.target.value;
          onReasonChange(value);
          onReasonError(validateReason(value));
        }}
        onBlur={(event) => {
          const trimmed = normalizeDrawerText(event.target.value);
          if (trimmed !== reason) onReasonChange(trimmed);
          onReasonError(validateReason(trimmed));
        }}
        slotProps={inputSlotProps}
        sx={sxProps}
        testId={`${testIdPrefix}-reason-input`}
      />
      {reasonError && (
        <p style={styles.errorText} className="text-sm mt-1">
          {reasonError}
        </p>
      )}
      <FormHelperText sx={charCounterSx}>
        {reason.length}/{REASON_TEXT_MAX_CHARS}
      </FormHelperText>
    </div>
  );
};

export default BulkSecurityClearanceReasonField;
